/**
 * Leitura do texto do edital ou do termo de referência (D94): sugere prazos e confere a descrição dos itens do PNCP
 * com o texto do documento. Tudo é sugestão: o usuário confere e aplica. Nada é deduzido sem trecho de origem.
 */

/** Letra a letra, sem acento e minúscula (mantém as posições do texto original). */
function normalizarLetra(c: string): string {
  const base = c.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  return base.length === 1 ? base : c.toLowerCase().slice(0, 1) || " ";
}
const normalizar = (t: string) => Array.from(t).map(normalizarLetra).join("");

/** Junta quebras de linha e espaços repetidos (o texto do PDF vem picado). */
export function limparTexto(texto: string): string {
  return texto.replace(/­/g, "").replace(/-\n(?=[a-zà-ú])/g, "").replace(/\s+/g, " ").trim();
}

export type CampoPrazo = "prazo_entrega_dias" | "prazo_pagamento_dias" | "vigencia_ata_meses";

export interface SugestaoPrazo {
  campo: CampoPrazo;
  valor: number;
  /** Dias úteis (o campo do sistema é em dias; o usuário decide). */
  uteis: boolean;
  /** Quantas vezes o mesmo valor aparece no documento. */
  ocorrencias: number;
  /** Trecho do documento de onde saiu o valor. */
  trecho: string;
  /** Outros valores encontrados para o mesmo campo (documento com prazos diferentes por item ou por tipo). */
  outros: number[];
}

const PISTAS: { campo: CampoPrazo; chave: RegExp; unidade: "dias" | "meses" }[] = [
  { campo: "prazo_entrega_dias", chave: /\b(prazo (maximo )?(de|para) (a )?entrega|entregue[s]? (em|no prazo)|entrega (devera|sera|dos? (bens|produtos|materiais|itens))|prazo de fornecimento)/g, unidade: "dias" },
  { campo: "prazo_pagamento_dias", chave: /\b(pagamento (sera|devera|ocorrera|efetuado|realizado)|prazo (maximo )?(de|para) (o )?pagamento|pago (em|no prazo))/g, unidade: "dias" },
  { campo: "vigencia_ata_meses", chave: /\b(vigencia da ata|ata de registro de precos? (tera|vigorara|tera validade|tera vigencia)|validade da ata|prazo de vigencia da ata)/g, unidade: "meses" },
];

/** Número antes da unidade: "10 (dez) dias", "30 dias", "12 (doze) meses", "1 (um) ano". */
const QUANTIDADE = /(\d{1,3})\s*(?:\([a-z\s]+\))?\s*(dias?|mes(?:es)?|anos?)(\s+(?:uteis|corridos|consecutivos))?/g;

const JANELA = 220;

export function extrairPrazosEdital(textoOriginal: string): SugestaoPrazo[] {
  const original = limparTexto(textoOriginal);
  const texto = normalizar(original);
  const sugestoes: SugestaoPrazo[] = [];
  for (const pista of PISTAS) {
    const achados: { valor: number; uteis: boolean; inicio: number; fim: number }[] = [];
    for (const m of texto.matchAll(pista.chave)) {
      const inicio = m.index ?? 0;
      const janela = texto.slice(inicio, inicio + JANELA);
      // Para no fim da frase: o número precisa estar na mesma frase da pista.
      const frase = janela.split(/(?<=[a-z0-9)])\.\s|;/)[0] ?? janela;
      QUANTIDADE.lastIndex = 0;
      const q = QUANTIDADE.exec(frase);
      if (!q) continue;
      const numero = Number(q[1]);
      const unidade = q[2] ?? "";
      let valor: number | null = null;
      if (pista.unidade === "dias" && unidade.startsWith("dia")) valor = numero;
      if (pista.unidade === "meses" && unidade.startsWith("mes")) valor = numero;
      if (pista.unidade === "meses" && unidade.startsWith("ano")) valor = numero * 12;
      if (valor === null || valor <= 0 || valor > 400) continue;
      achados.push({ valor, uteis: (q[3] ?? "").includes("uteis"), inicio, fim: inicio + (q.index ?? 0) + q[0].length });
    }
    if (achados.length === 0) continue;
    const contagem = new Map<number, number>();
    for (const a of achados) contagem.set(a.valor, (contagem.get(a.valor) ?? 0) + 1);
    const [valor, ocorrencias] = [...contagem.entries()].sort((a, b) => b[1] - a[1] || achados.findIndex((x) => x.valor === a[0]) - achados.findIndex((x) => x.valor === b[0]))[0] ?? [0, 0];
    const primeiro = achados.find((a) => a.valor === valor);
    if (!primeiro) continue;
    sugestoes.push({
      campo: pista.campo,
      valor,
      uteis: primeiro.uteis,
      ocorrencias,
      trecho: original.slice(primeiro.inicio, Math.min(original.length, primeiro.fim + 40)).trim(),
      outros: [...contagem.keys()].filter((v) => v !== valor).sort((a, b) => a - b),
    });
  }
  return sugestoes;
}

/**
 * Último dia para impugnar ou pedir esclarecimento: até 3 dias úteis antes da abertura (Lei 14.133/2021, art. 164).
 * Conta só sábados e domingos como não úteis: feriados precisam ser conferidos.
 * Recebe e devolve datas "aaaa-mm-dd".
 */
export function prazoImpugnacao(dataAbertura: string, diasUteis = 3): string {
  const [a, m, d] = dataAbertura.slice(0, 10).split("-").map(Number);
  if (!a || !m || !d) throw new Error("Data de abertura inválida.");
  const data = new Date(Date.UTC(a, m - 1, d));
  let restantes = diasUteis;
  while (restantes > 0) {
    data.setUTCDate(data.getUTCDate() - 1);
    const dia = data.getUTCDay();
    if (dia !== 0 && dia !== 6) restantes -= 1;
  }
  return data.toISOString().slice(0, 10);
}

/* ------------------------------ Conferência dos itens com o documento ------------------------------ */

export type SituacaoConferencia = "confere" | "divergente" | "nao_localizado";

export interface ConferenciaItem {
  situacao: SituacaoConferencia;
  /** Especificações da descrição do PNCP que não aparecem no trecho do documento. */
  faltando: string[];
  /** Especificações do trecho do documento que não estão na descrição do PNCP. */
  extras: string[];
  /** Trecho do documento mais parecido com o item. */
  trecho: string | null;
  /** Fração das palavras do item encontradas no trecho (0 a 1). */
  semelhanca: number;
  /** Por que não foi possível conferir. */
  motivo: "descricao_curta" | "sem_trecho_parecido" | null;
}

/** Mínimo de palavras significativas na descrição do PNCP para localizar o item no documento. */
const MINIMO_PALAVRAS = 4;
/** Fração mínima das palavras do item que precisam estar no mesmo trecho do documento. */
const SEMELHANCA_MINIMA = 0.6;

const IGNORAR = new Set([
  "para", "com", "sem", "tipo", "cor", "material", "conforme", "marca", "modelo", "unidade", "minimo", "minima", "maximo",
  "maxima", "superior", "inferior", "pelo", "pela", "menos", "mais", "deve", "devera", "sera", "como", "entre", "sobre",
  "deste", "desta", "estes", "estas", "item", "itens", "produto", "novo", "nova", "primeiro", "primeira", "qualidade", "uso",
]);

const palavras = (t: string) =>
  normalizar(t)
    .split(/[^a-z0-9]+/)
    .filter((p) => p.length >= 4 && !IGNORAR.has(p) && !/^\d+$/.test(p));

const UNIDADES =
  "gb|tb|mb|kb|ghz|mhz|hz|w|kw|va|kva|v|mah|wh|ah|mm|cm|m|km|kg|g|mg|l|ml|rpm|dpi|ppm|mbps|gbps|mb/s|gb/s|pol|polegadas|bits?|bytes?|nucleos|cores|portas|vias|pecas|folhas|metros|litros|watts|volts";
const ESPEC = new RegExp(`(\\d+(?:[.,]\\d+)?)\\s*(${UNIDADES})(?![a-z0-9])`, "g");
const CODIGO = /\b(?=[a-z0-9-]*\d)(?=[a-z0-9-]*[a-z])[a-z0-9]+(?:-[a-z0-9]+)*\b/g;

/** Especificações com número e unidade ("16 gb", "3.0"…) e códigos (part number, modelo). */
export function especificacoes(texto: string): string[] {
  const t = normalizar(texto);
  const achadas = new Set<string>();
  for (const m of t.matchAll(ESPEC)) {
    const bruto = m[1] ?? "";
    // "3.420" (milhar) → 3420; "2,5" → 2.5.
    const numero = /^\d{1,3}(\.\d{3})+$/.test(bruto) ? bruto.replace(/\./g, "") : bruto.replace(",", ".");
    achadas.add(`${numero} ${m[2] ?? ""}`);
  }
  for (const m of t.matchAll(CODIGO)) {
    const c = m[0];
    if (c.length >= 5 && !/^\d+(gb|tb|mb|kg|mm|cm|ml|mah|wh|w|v|g|l|m)$/.test(c)) achadas.add(c);
  }
  return [...achadas];
}

/** Divide o documento em trechos (parágrafos ou frases longas) para localizar cada item. */
function trechosDoDocumento(texto: string): string[] {
  const limpo = limparTexto(texto);
  const partes = limpo.split(/(?<=[.;:])\s+(?=(?:\d{1,3}[.)-]\s|[A-ZÀ-Ú]))/);
  // Janelas de até 3 partes seguidas: a descrição do item pode ocupar mais de uma frase.
  const janelas: string[] = [];
  for (let i = 0; i < partes.length; i++) {
    janelas.push(partes.slice(i, i + 3).join(" ").slice(0, 1500));
  }
  return janelas;
}

/**
 * Confere a descrição do item (PNCP) com o documento: localiza o trecho mais parecido e compara as especificações.
 * "nao_localizado" = o documento não tem texto parecido (escaneado, item descrito em outro anexo…): precisa analisar.
 */
export function conferirItem(descricaoItem: string, documento: string | string[]): ConferenciaItem {
  const janelas = Array.isArray(documento) ? documento : trechosDoDocumento(documento);
  const doItem = [...new Set(palavras(descricaoItem))];
  // Descrição curta ("Microfone"): não há como localizar o item com segurança no documento.
  if (doItem.length < MINIMO_PALAVRAS || janelas.length === 0)
    return { situacao: "nao_localizado", faltando: [], extras: [], trecho: null, semelhanca: 0, motivo: "descricao_curta" };
  let melhor = { indice: -1, semelhanca: 0 };
  janelas.forEach((j, indice) => {
    const presentes = new Set(palavras(j));
    const semelhanca = doItem.filter((p) => presentes.has(p)).length / doItem.length;
    if (semelhanca > melhor.semelhanca) melhor = { indice, semelhanca };
  });
  const trecho = janelas[melhor.indice] ?? null;
  if (!trecho || melhor.semelhanca < SEMELHANCA_MINIMA)
    return { situacao: "nao_localizado", faltando: [], extras: [], trecho: null, semelhanca: melhor.semelhanca, motivo: "sem_trecho_parecido" };
  const especItem = especificacoes(descricaoItem);
  const especTrecho = especificacoes(trecho);
  const noTrecho = new Set(especTrecho);
  const noItem = new Set(especItem);
  const faltando = especItem.filter((e) => !noTrecho.has(e));
  // Só conta como extra a especificação da mesma grandeza de uma que falta (ex.: 16 gb × 32 gb).
  const unidadesFaltando = new Set(faltando.map((e) => e.split(" ")[1]).filter(Boolean));
  const extras = especTrecho.filter((e) => !noItem.has(e) && unidadesFaltando.has(e.split(" ")[1]));
  return {
    situacao: faltando.length > 0 ? "divergente" : "confere",
    faltando,
    extras,
    trecho,
    semelhanca: Math.round(melhor.semelhanca * 100) / 100,
    motivo: null,
  };
}

/** Trechos do documento prontos para conferir vários itens (evita dividir o texto a cada item). */
export function prepararDocumento(texto: string): string[] {
  return trechosDoDocumento(texto);
}
